import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  DownloadOutlined,
  ImageOutlined,
  TableChartOutlined,
  CheckCircle,
} from '@mui/icons-material';
import './ExportMenu.css';

/**
 * ExportMenu — bouton d'export "speed-dial" pour un graphique.
 * Au survol (ou clic), déploie les actions disponibles (CSV / PNG)
 * en éventail. Chaque action affiche un état de succès transitoire (✓).
 */
const ExportMenu = ({
  onExportPNG,
  onExportCSV,
  color = '#0EA5E9',
  size = 'md',
  label = 'Exporter',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [justDone, setJustDone] = useState(null); // 'png' | 'csv' | null
  const rootRef = useRef(null);
  const closeTimer = useRef(null);

  const actions = [
    onExportCSV ? { key: 'csv', icon: <TableChartOutlined />, run: onExportCSV, title: 'Exporter en CSV' } : null,
    onExportPNG ? { key: 'png', icon: <ImageOutlined />, run: onExportPNG, title: 'Exporter en image (PNG)' } : null,
  ].filter(Boolean);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleAction = useCallback((action) => {
    action.run?.();
    setJustDone(action.key);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setJustDone(null);
      setOpen(false);
    }, 850);
  }, []);

  if (actions.length === 0) return null;

  return (
    <div
      ref={rootRef}
      className={`exm-root exm-root--${size}${open && !disabled ? ' exm-root--open' : ''}${disabled ? ' exm-root--disabled' : ''}`}
      style={{ '--exm-color': color }}
      onMouseEnter={() => !disabled && setOpen(true)}
      onMouseLeave={() => !justDone && setOpen(false)}
    >
      <button
        type="button"
        className="exm-trigger"
        title={disabled ? `${label} (indisponible)` : label}
        aria-label={label}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <DownloadOutlined className="exm-trigger-icon" />
      </button>

      <div className="exm-flyout" role="menu">
        {actions.map((action, idx) => (
          <button
            key={action.key}
            type="button"
            role="menuitem"
            className={`exm-action${justDone === action.key ? ' exm-action--done' : ''}`}
            style={{ '--exm-delay': `${idx * 40}ms` }}
            title={action.title}
            disabled={disabled}
            onClick={() => handleAction(action)}
          >
            {justDone === action.key
              ? <CheckCircle className="exm-action-icon exm-action-icon--check" />
              : <span className="exm-action-icon">{action.icon}</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ExportMenu;